import { Add } from 'iconsax-react';
import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useLoaderData, useNavigate } from 'react-router';
import { useTypedHash, useTypedSearchParams } from 'react-router-typesafe-routes';
import { Virtuoso } from 'react-virtuoso';
import type { ModalAction } from '@/components/common/modal/modal.type';
import TabList from '@/components/common/tablist';
import EmptyLayout from '@/components/layouts/empty-layout';
import PageLayout from '@/components/layouts/page-layout/page-layout';
import LoadingLayout from '@/components/layouts/progress-layout/loading-layout';
import CollectionItem from '@/components/user/collection-item/collection-item';
import CollectionMenuModal from '@/components/user/collection-menu-modal';
import { CollectionMenuProvider } from '@/components/user/collection-menu-modal/hook';
import type { Collection } from '@/hooks/collections/use-collections';
import useFilteredCollections, {
  type CollectionFilterReducerFilterType,
} from '@/hooks/collections/use-filtered-collections';
import { useCommDispatcher } from '@/hooks/comm/use-comm';
import { useFriendBySessionId } from '@/hooks/contacts/use-friends';
import useBatchForward from '@/hooks/forward/use-batch-forward';
import useGroupInfo from '@/hooks/groups/use-group-info';
import useScrollRestoration from '@/hooks/use-scroll-restoration';
import { useIsDesktop } from '@/hooks/use-user-platform';
import type { ConversationType } from '@/models/conversations/conversation-session';
import ROUTES, { type PromiseLoaderReturnType } from '@/routes';
import { parseCollectionDate } from '@/utils/parseTime';
import { generateSessionKey } from '@/utils/session';

interface Props {
  type?: ConversationType;
  id?: number;
}

export default function CollectionsIndexRoute({ type: sessionType, id: sessionId }: Props) {
  const loaderData = useLoaderData<PromiseLoaderReturnType>();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const hash = useTypedHash(ROUTES.MY.COLLECTIONS);
  const menuModalRef = useRef<ModalAction>(null);
  const [selected, setSelected] = useState<Collection | null>(null);
  const [position, setPosition] = useState<[number, number] | null>(null);
  const { filteredCollections, filterType, setFilterType } = useFilteredCollections(hash);
  const { mutateAsyncWithPrompt: forwardCollections } = useBatchForward();
  const { friend } = useFriendBySessionId(sessionType === 'session' ? sessionId : null);
  const { data: group } = useGroupInfo(sessionType === 'group' ? sessionId : null);
  const { dispatch } = useCommDispatcher();
  const [searchParams] = useTypedSearchParams(ROUTES.MY.COLLECTIONS);

  const isDesktop = useIsDesktop();
  const isDesktopModal = Boolean(isDesktop && sessionType);

  useScrollRestoration();

  const handleChangeFilter = (type: CollectionFilterReducerFilterType) => {
    setFilterType(type);
    if (sessionId) {
      const route =
        sessionType === 'session' ? ROUTES.MESSAGES.SESSIONS.COLLECTIONS : ROUTES.MESSAGES.GROUPS.COLLECTIONS;
      const url = route.$buildPath({
        params: { id: sessionId },
        hash: type === 'all' ? undefined : type,
      });
      history.replaceState(null, '', url);
    } else {
      const url = ROUTES.MY.COLLECTIONS.$buildPath({ searchParams, hash: type === 'all' ? undefined : type });
      navigate(url, { replace: true });
    }
  };

  const handleForward = async (collectionId: number) => {
    if (sessionType && sessionId) {
      const key = generateSessionKey(sessionType, sessionId);
      await forwardCollections({
        collectionIds: [collectionId],
        targets: [
          sessionType === 'group'
            ? { key, type: 'group', name: group?.GroupData?.GroupName ?? '', groupId: sessionId }
            : {
                key,
                type: 'user',
                name: friend?.RemarkName || friend?.FriendNickName || '',
                userId: friend?.FriendID ?? 0,
              },
        ],
      });
    } else {
      navigate(ROUTES.MY.COLLECTIONS.BY_ID.FORWARD.$buildPath({ params: { collectionId } }));
    }
  };

  return (
    <PageLayout
      title={t('my-collection.title')}
      headerConfig={{
        navigateBack: !isDesktopModal,
        onNavigateBack: () => (searchParams.from === 'onboard' ? dispatch({ event: 'dismiss' }) : undefined),
        className: 'desktop:p-0',
        elementAfterNav: (
          <TabList
            className='mt-4'
            tabs={filterOptions}
            defaultActiveTab={filterType}
            onClickTab={handleChangeFilter}
          />
        ),
        actions: isDesktopModal && (
          <button
            type='button'
            className='btn btn-ghost p-0'
            onClick={() => navigate(-1)}>
            <Add className='rotate-45' />
          </button>
        ),
      }}>
      <div className='flex flex-1 w-full max-w-screen-md flex-col gap-4 desktop:max-w-full'>
        <CollectionMenuProvider
          value={{
            selected,
            position,
            setSelected: (collection, position) => {
              setSelected(collection);
              setPosition(position ?? null);
              menuModalRef.current?.open();
            },
            forward: handleForward,
          }}>
          <LoadingLayout promise={loaderData?.promises}>
            {() => (
              <Virtuoso
                className='flex-1'
                data={Object.entries(filteredCollections)}
                components={{
                  EmptyPlaceholder: () => <EmptyLayout label='my-collection.empty' />,
                }}
                itemContent={(_, [key, items]) => (
                  <section
                    key={key}
                    className='mx-5 desktop:mx-0'>
                    <div className='flex'>
                      <p className='py-2 text-sm'>{items?.[0]?.FromName}</p>
                      <p className='ml-4 py-2 text-sm'>{parseCollectionDate(items?.[0]?.TimeStamp ?? 0)}</p>
                    </div>

                    <div className='flex snap-x snap-proximity gap-4 overflow-x-auto'>
                      {items?.map((item) => {
                        const href =
                          sessionType && sessionId
                            ? (sessionType === 'session'
                                ? ROUTES.MESSAGES.SESSIONS.COLLECTIONS.BY_ID
                                : ROUTES.MESSAGES.GROUPS.COLLECTIONS.BY_ID
                              ).$buildPath({
                                params: {
                                  id: sessionId,
                                  collectionId: item.ID,
                                },
                              })
                            : ROUTES.MY.COLLECTIONS.BY_ID.$buildPath({
                                params: {
                                  collectionId: item.ID,
                                },
                              });

                        return (
                          <Link
                            key={item.ID}
                            className='snap-start last:snap-end'
                            to={href}>
                            <CollectionItem collection={item} />
                          </Link>
                        );
                      })}
                    </div>
                  </section>
                )}
              />
            )}
          </LoadingLayout>

          <CollectionMenuModal
            ref={menuModalRef}
            onClose={() => {
              setSelected(null);
              setPosition(null);
              menuModalRef.current?.close();
            }}
            contextMenuType={sessionType ? 'forward' : 'normal'}
          />
        </CollectionMenuProvider>
      </div>
    </PageLayout>
  );
}

const filterOptions: Array<{ key: CollectionFilterReducerFilterType; label: string }> = [
  {
    key: 'all',
    label: 'my-collection.all',
  },
  {
    key: 'text',
    label: 'my-collection.text',
  },
  {
    key: 'media',
    label: 'my-collection.media',
  },
  {
    key: 'files',
    label: 'my-collection.file',
  },
];
